import { returnAllTradeDetailsFromGenericParams } from "@/lib/agGridColDefs/colDefGlobalHelpers";
import { isValidStirke } from "@/lib/businessRules/strikeValueCells";
import tradeDataStore from "@/store/tradeDataStore";
import { updateExecutionEditSecurity } from "@/store/tradeDataStoreACTIONS";
import { useAuth0 } from "@auth0/auth0-react";
import { useTheme } from "next-themes";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";
import {
  isCPIEditable,
  returnEridFromBlock,
} from "../actionSummaryColDefHelperFunctions";
import { returnCPIValueToRender } from "../cellRenderers/CPICellRenderer";
import { truncateValueIfNeeded } from "./DynamicCellEditorQuoteDollarFractionHelper";

export function returnCPIValueOnMount() {}

const CPICellEditor = forwardRef((params: any, ref) => {
  const { getAccessTokenSilently } = useAuth0();
  const { theme } = useTheme();

  const { isOp, isOutright, fullTradeData } =
    returnAllTradeDetailsFromGenericParams(params);
  const currentTradeData = tradeDataStore((state) => state.tradeData);
  const thisTrade = currentTradeData.find(
    (trade) => trade.esid == fullTradeData.esid,
  );

  const valueToReturn: number =
    (thisTrade && returnCPIValueToRender(params, thisTrade)) || "";

  const [value, setValue] = useState(valueToReturn || "");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  //   let notation: NotationToDecimalValueType = "strike";

  const handleInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;
    if (!isValidStirke(event.target.value)) {
      return; // return early and don't set the value if the input does not match rules.
    }
    const inputEvent = event.nativeEvent as InputEvent;

    if (input == "") {
      if (inputEvent.inputType != "deleteContentBackward") {
        return;
      }
      setValue(input);
      return;
    }

    if (input.toString().includes(".")) {
      //   let splits = input.split("."); //splits will only have 2 options. 2nd will be after the .
      //   let maxNumberOfDecimalPlaces = formatNumberBasedOnValue(notation);
      //   let numOfValuesAfterDecimal = splits[1].length;
      // Early retrun to prevent setting value (cut of the user from entering more values.)
      // if (numOfValuesAfterDecimal > maxNumberOfDecimalPlaces) {
      //   if (inputEvent.inputType == "insertFromPaste") {
      //     toast.error(
      //       `Maximum of ${maxNumberOfDecimalPlaces} decimal places allowed for ${notation} input.`,
      //     );
      //     return;
      //   }
      //   if (inputEvent.inputType != "deleteContentBackward") {
      //     // Allow backspaces to be entered.
      //     return;
      //   }
      // }
    }
    setValue(input);
  };

  const validateAndSave = () => {
    const valueHasNotBeenChanged = valueToReturn == value;
    if (valueHasNotBeenChanged) {
      return Number(value);
    }

    //return early if the input value is the same as starting value.
    if (Number(value) < 0 || value === null) {
      toast.error(`Strike value is too small and set back to ${valueToReturn}`);
      return valueToReturn;
    } else if (valueToReturn == value) {
      return Number(value);
    }
    if (Number(value) > 900) {
      toast.error(
        `Strike value exceeds maximum of 900 and set back to ${valueToReturn}`,
      );
      return Number(valueToReturn);
    }

    const updatedValue = truncateValueIfNeeded(value, "default").replace(
      /,/g,
      "",
    );

    if (isOp && isOutright) {
      updateExecutionEditSecurity({
        esid: fullTradeData.esid,
        erid: params.data.executionRequestId,
        blockid: params.data.legs?.[0]?.blockId,
        cpi: Number(updatedValue),
        getAccessTokenSilently: getAccessTokenSilently,
      });
      // UPDATE on the Block Level for StructureTypes that are accepted.
    } else if (isOp && !isOutright) {
      updateExecutionEditSecurity({
        esid: fullTradeData.esid,
        erid: returnEridFromBlock(fullTradeData, params.data.blockId),
        blockid: params.data.blockId,
        cpi: Number(updatedValue),
        getAccessTokenSilently: getAccessTokenSilently,
      });
      // RP Outright
    } else if (!isOp && isOutright) {
      updateExecutionEditSecurity({
        esid: fullTradeData.esid,
        erid: params.data.executionRequestId,
        blockid: params.data.legs?.[0]?.blockId,
        cpi: Number(updatedValue),
        getAccessTokenSilently: getAccessTokenSilently,
      });
      // RP Leg
    } else if (!isOp && !isOutright) {
      updateExecutionEditSecurity({
        esid: fullTradeData.esid,
        erid: returnEridFromBlock(fullTradeData, params.data.blockId),
        blockid: params.data.blockId,
        cpi: Number(updatedValue),
        getAccessTokenSilently: getAccessTokenSilently,
      });
    }
    return Number(updatedValue);
  };

  useImperativeHandle(ref, () => ({
    getValue: () => validateAndSave(),
  }));

  // CPI
  const isEditable = isCPIEditable({
    esid: fullTradeData?.esid,
    thisTradePassed: thisTrade,
  });

  function editableStyle() {
    if (isEditable && theme == "light") {
      return "inputCellOtherLight roundedInputCell";
    } else if (isEditable && theme == "dark") {
      return "inputCellOtherDark roundedInputCell";
    }
  }

  return (
    <input
      ref={inputRef}
      type="number"
      value={
        value && value == valueToReturn
          ? truncateValueIfNeeded(value.toString(), "default")
          : value
      }
      onChange={handleInput} // Blocks unwanted characters immediately
      className={`ag-roundedInputStyle ag-input text-center ${editableStyle()}`}
    />
  );
});

export default CPICellEditor;
